import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { queryClient } from '../constants.js';
import type { Query } from '@tanstack/react-query';
import { queryPolicies } from '../queryPolicies.js';

// Type definitions for cache operations - React Query v5 compatible
interface QueryOptions {
  staleTime: number;
  gcTime: number;                    // cacheTime renamed to gcTime in v5
  refetchOnWindowFocus: boolean;     // revalidateOnFocus renamed in v5
  refetchOnReconnect: boolean;       // revalidateOnReconnect renamed in v5
}

// React Query v5 compatible configuration
export const commonQueryProps: QueryOptions = {
  staleTime: queryPolicies.metadata.staleTime,
  gcTime: queryPolicies.metadata.gcTime,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true
};

// Make queryClient available globally for debugging
declare global {
  interface Window {
    queryClient: typeof queryClient;
  }
}

window.queryClient = queryClient;

export const postgrestQueryKey = (query: any) => encode(query, false);

/**
 * Invalidates and refetches queries for a specific PostgrestBuilder query
 * @param query - The PostgrestBuilder query to invalidate
 */
export const invalidateAndRefetch = (query: any): void => {
  const queryKey = postgrestQueryKey(query);
  queryClient.invalidateQueries({
    queryKey,
    exact: false,
    refetchType: 'active'
  });
};

/**
 * Invalidates all metadata cache entries in the correct dependency order
 * @returns Promise that resolves when all invalidations are complete
 */
export const invalidateAllMetadataCache = async (): Promise<PromiseSettledResult<any>[]> => {

  // when we first wrote this function, we just invalidated all the metadata tables as a batch.
  // there were some bugs, so we added the more granular approach below.
  // this whole time refetchType was set to 'active' while it should have been 'all'
  // TODO, investigate if we can just use 'all' for all of these instead of batching invalidations
  
  // this reflects the dependency chain of the metadata tables
  // e.g. table_ui_component depends on table info so we need to invalidate table_info first
  // table_route depends on both table_info and table_ui_component, etc.
  const metadataTables: string[] = [
    'table_field', 
    'table_info', 
    'relationship_lookup', 
    'constraint_info', 
    'global_setting', 
    'picklist_view', 
    'table_ui_component', 
    'preset_table_filter', 
    'table_route',
    'report_info',
    'report_field',
    'report_parameter',
    'report_route',
    'ai_memory',
    'dashboard_info'
  ];

  const invalidationPromises = metadataTables.map((table: string) =>
    queryClient.invalidateQueries({
      predicate: (query: Query) => Array.isArray(query.queryKey) && query.queryKey[3] === table,
      refetchType: 'all',
    })
  );
  return Promise.allSettled(invalidationPromises);
};

/**
 * Invalidates all queries for a specific table without refetching
 * @param tableName - The name of the table to invalidate queries for
 */
export const invalidateAllQueriesForTableNoRefetch = (tableName: string): void => {
  queryClient.invalidateQueries({
    predicate: (query: Query) => {
      const key = query.queryKey;
      if (Array.isArray(key) && key[0] === 'report-rows') return true;
      // Cache key structure: ["postgrest", "null", "schema_name", "table_name", ...]
      // Check if index 3 matches our table name, table_view, or table_deep_view
      const matches = Array.isArray(key) && 
             (key[3] === tableName || 
              key[3] === `${tableName}_view` || 
              key[3] === `${tableName}_deep_view`);
      return matches;
    },
    refetchType: 'none',
  });
};

/**
 * Invalidates and refreshes all queries for a specific table
 * @param tableName - The name of the table to invalidate and refresh queries for
 * @returns Promise that resolves when invalidation is complete
 */
export const invalidateAndRefreshAllQueriesForTable = async (tableName: string): Promise<void> => {
  return await queryClient.invalidateQueries({
    predicate: (query: Query) => {
      const key = query.queryKey;
      if (Array.isArray(key) && key[0] === 'report-rows') return true;
      // Cache key structure: ["postgrest", "null", "schema_name", "table_name", ...]
      // Check if index 3 matches our table name, table_view, or table_deep_view
      return Array.isArray(key) && 
             (key[3] === tableName || 
              key[3] === `${tableName}_view` || 
              key[3] === `${tableName}_deep_view`);
    },
    refetchType: 'active'
  });
};

/**
 * Invalidates a specific query without refetching
 * @param query - The PostgrestBuilder query to invalidate
 */
export const invalidateQueryNoRefetch = (query: any): void => {
  const queryKey = postgrestQueryKey(query);
  queryClient.invalidateQueries({
    queryKey,
    exact: false,
    refetchType: 'none'
  });
};
